import {
  FieldTypes,
  IField,
  ISortedField,
  SectionId,
  V2,
  ValidationTypes,
  deleteObjectById,
  duplicateObject,
  generateID,
  getJsonAltKeyFromField,
  getJsonKeyFromField,
  getObjectById,
  sortFields,
  spErrorTags,
  structuredCloneL,
  tryParseJSON,
} from "@eventr/common";
import { TDialogStore, useDialogStore, useValidatorContext } from "@eventr/common-storybook";
import i18n from "i18n/i18next";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { routes } from "routing";
import { useShallow } from "zustand/react/shallow";
import { ISection } from "../../data/Data";
import { bs, useBoundStore } from "../../state/bound.store";
import { selectCurrentItineraryTitle } from "../../state/itineraryState/currentItin.slice";
import { defaultAddSnack } from "../../state/overlayState/overlayContext";
import { sectionInfoToEmptyV2 } from "../../utils";
import { collectDraftItemIds, DraftItem, findTopLevelEntry, mergeDraftItems } from "../../utils/merge-draft-items";
import { useGlobalRouter } from "../globalRouter/useGlobalRouter";
import { differsFromNewItemSeed, recordNewItemSeed } from "./new-item-seeds";

interface IEditSectionContext {
  sectionData?: V2;
  section?: ISection;
  updateValues: (value: IUpdateValue) => void;
  deleteSetValue?: (value: IUpdateValue) => void;
  createNewItem?: (id: string, listId?: string, field?: IField) => void;
  syncDraftItem?: (id: string) => Promise<void>;
  discardDraftItem?: (id: string) => Promise<void>;
  getValue: (field: IField, _id?: string, prettyPrint?: boolean, altValue?: boolean) => any;
  values?: any;
  isListTypeSection?: boolean;
  onCancelEdit?: () => void;
  // Runs onSaveData's validation and error-surfacing (red fields, toast) without saving.
  validateForSave?: (id: string) => boolean;
  onSaveData?: (id: string, options?: { skipValidation?: boolean; skipNavigation?: boolean }) => Promise<boolean>;
  // Whether the item of this id differs from the data the section was loaded with.
  hasPendingChanges?: (id: string) => boolean;
  isNewItem: (id: string) => boolean;
  itineraryTitle?: string;
  setNewItinMode?: (value: boolean) => void;
  alreadyPopulated?: boolean;
  nowSetAlreadyPopulated?: (value: boolean) => void;
  deleteItem?: (_id: string) => void;
  duplicateItem?: (_id: string) => void;
  swipeDeleteItem?: (_id: string) => boolean;
  swipeDeleteCommit?: (_id: string) => void;
  swipeDuplicateItem?: (_id: string) => void;
}

interface IUpdateValue {
  value: any;
  field?: IField;
  _id?: string;
  valueAlt?: any;
}

const prefillRules: Record<number, string> = {
  228: "array",
  229: "itinMembers",
  311: "array",
  312: "itinMembers",
  314: "itinStart",
  315: "itinEnd",
  417: "array",
  418: "itinMembers",
  407: "itinStart",
  409: "itinEnd",
  515: "array",
  516: "itinMembers",
  502: "itinStart",
  504: "itinEnd",
  808: "array",
  809: "itinMembers",
  802: "itinStart",
  903: "array",
  905: "itinStart",
  906: "itinEnd",
  911: "itinMembers",
};

// A cleared address keeps a bare timeZone key, so plain truthiness reads it as present.
const isFieldValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    if ("timeZone" in value || "lat" in value || "lon" in value) return !(value.lat && value.lon);
    return Object.keys(value).length === 0;
  }
  return false;
};

// Checked independently of validator registration, so no field type or button can skip it.
const isFieldTreeComplete = (
  sortedField: ISortedField,
  itemId: string,
  getValue: IEditSectionContext["getValue"],
): boolean => {
  const { field, children } = sortedField;
  if (field.field_type === FieldTypes.list) {
    // Each entry (e.g. a booking) owns its values under its own _id, so recurse per entry.
    const rawEntries = getValue(field, itemId);
    const entries = Array.isArray(rawEntries) ? rawEntries : [];
    return entries.every((entry: { _id: string }) =>
      children.every((child) => isFieldTreeComplete(child, entry._id, getValue)),
    );
  }

  // GroupField (e.g. address) stores its children under its own generated _id, not the
  // parent item's - see group.tsx. Reading a group's children against itemId always finds
  // them missing, since their json keys never live on the parent object.
  let childItemId = itemId;
  if (field.field_type === FieldTypes.group) {
    childItemId = getValue(field, itemId)?._id ?? itemId;
  } else {
    const isRequired = field.type_properties?.validations?.some((v) => v.type === ValidationTypes.required);
    if (isRequired && isFieldValueEmpty(getValue(field, itemId))) {
      spErrorTags(`Required field empty: "${field.field_name}" (id ${field.id}) on item ${itemId}`, ["SAVE"]);
      return false;
    }
  }

  return children.every((child) => isFieldTreeComplete(child, childItemId, getValue));
};

const EditSectionContext = createContext<IEditSectionContext>({
  sectionData: undefined,
  section: undefined,
  updateValues: () => {},
  deleteSetValue: undefined,
  createNewItem: undefined,
  syncDraftItem: undefined,
  discardDraftItem: undefined,
  getValue: () => null,
  values: undefined,
  isListTypeSection: undefined,
  onCancelEdit: undefined,
  validateForSave: () => true,
  isNewItem: () => false,
});

export const EditSectionProvider = (props: { children: React.ReactNode }) => {
  const { sectionId: sectionID, isNew } = useParams<{
    sectionId: string;
    isNew: string;
  }>();

  const itineraryTitle = useBoundStore(selectCurrentItineraryTitle);
  // Top-level reference: saves (updateItinerarySection) and refetches replace it, even
  // though the section objects it holds are mutated in place.
  const selectedItinerary = useBoundStore((s) => s.selectedItinerary);

  const {
    getEventInfoV2,
    updateItinerarySection,
    getFieldsForSection,
    sections,
    sectionDraftItems,
  } = useBoundStore(
    useShallow((s) => {
      const sectionIdNum = sectionID === undefined ? undefined : (Number.parseInt(sectionID) as SectionId);
      return {
        getEventInfoV2: s.getEventInfoV2,
        updateItinerarySection: s.updateItinerarySection,
        sections: s.sections,
        getFieldsForSection: s.getFieldsForSection,
        // Scoped to this provider's own section, so a draft write elsewhere does not
        // rebuild the working values of every mounted provider.
        sectionDraftItems:
          sectionIdNum === undefined ? undefined : s.draftSectionItems[s.selectedItinID]?.[sectionIdNum],
      };
    }),
  );

  const [section, setSection] = useState<ISection>();
  const [values, setValues] = useState<any>({});
  const [sectionData, setSectionData] = useState<V2>();
  const [alreadyPopulated, setAlreadyPopulated] = useState<boolean>(false);
  const { isPathValid, fieldStates, setShowErrors, setValidationOn } = useValidatorContext();
  const ogValues = useRef<any>({});
  // Every _id in ogValues, nested ones included.
  const ogIds = useRef<Set<string>>(new Set());
  const setOgValues = (next: any) => {
    ogValues.current = next;
    ogIds.current = new Set(collectDraftItemIds(next));
  };
  // Scoped to this view because window.goBack follows the frontmost view, which can change under an async operation.
  const { goBack: goBackInView, pushPath } = useGlobalRouter();
  const { replaceDialog, clearDialog } = useDialogStore(
    useShallow((s: TDialogStore) => ({
      clearDialog: s.clearDialog,
      replaceDialog: s.replaceDialog,
    })),
  );

  // Copies the store's section into sectionData, the list screen's render source.
  // Never touches values, so in-progress edits survive.
  const refreshSectionData = useCallback((): V2 | undefined => {
    if (sectionID === undefined) return undefined;
    const sectionV2Data = bs().getSectionV2FromSectionIdFromSelectedItinerary(Number.parseInt(sectionID));
    if (sectionV2Data === undefined) return undefined;
    setSectionData(structuredCloneL(sectionV2Data));
    return sectionV2Data;
  }, [sectionID]);

  useEffect(() => {
    initLoad();
    // selectedItinerary is deliberately not a dep: initLoad rebuilds values, which would
    // drop in-progress edits. sectionData is refreshed by the effect below instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionID, isNew]);

  // A sibling view's save or a refetch lands as a new selectedItinerary reference;
  // refresh the list's sectionData then.
  useEffect(() => {
    if (selectedItinerary !== undefined) refreshSectionData();
  }, [selectedItinerary, refreshSectionData]);

  useEffect(() => {
    const section = sections?.find((section) => {
      if (sectionID) {
        return section.id === Number.parseInt(sectionID);
      }
      return false;
    });

    setSection(section);
  }, [sectionID, sections]);

  const sortedFields = useMemo<ISortedField[]>(() => {
    if (sectionID === undefined) return [];
    return sortFields(getFieldsForSection(Number.parseInt(sectionID)), false);
  }, [getFieldsForSection, sectionID]);

  const initLoadKey = useRef<string | null>(null);

  const initLoad = () => {
    if (sectionID === undefined) return;
    const sectionIdNum = Number.parseInt(sectionID);

    let sectionV2Data = refreshSectionData();
    if (sectionV2Data === undefined) {
      const sectionInfo = bs().getSection(sectionIdNum);
      if (sectionInfo === undefined) throw new Error("Section not found");
      sectionV2Data = sectionInfoToEmptyV2(sectionInfo);
      setSectionData(structuredCloneL(sectionV2Data));
    }

    const loadKey = JSON.stringify([sectionID, isNew, sectionV2Data?.data]);
    if (loadKey === initLoadKey.current) return;
    initLoadKey.current = loadKey;

    if (isNew === "new") {
      setValues({});
      setOgValues({});
    } else {
      try {
        setOgValues(JSON.parse(JSON.stringify(sectionV2Data?.data)));
        setValues(
          mergeDraftItems(
            JSON.parse(JSON.stringify(sectionV2Data?.data)),
            topLevelJsonKey(),
            bs().draftSectionItems[bs().selectedItinID]?.[sectionIdNum as SectionId],
          ),
        );
      } catch {
        setValues((current: any) =>
          mergeDraftItems(
            current,
            topLevelJsonKey(),
            bs().draftSectionItems[bs().selectedItinID]?.[sectionIdNum as SectionId],
          ),
        );
      }
    }
  };

  const updateValues = useCallback(
    (value: IUpdateValue) => {
      // A caller that names a field but hands over undefined resolved it against a field
      // registry that does not carry that id - registries are fetched per environment, so
      // this differs between LOCAL and TEST. Without this guard the write falls through to
      // the whole-item branch below and replaces the item with the raw value, which throws
      // on a later render rather than here, far from the cause.
      if ("field" in value && !value.field) {
        spErrorTags(`updateValues: field missing from registry, write dropped on item ${value._id}`, ["SAVE"]);
        return;
      }
      if (value._id) {
        let obj = getObjectById(value._id, values);
        if (obj) {
          if (value.field) {
            obj[getJsonKeyFromField(value.field)] = value.value;

            if (value.field.id === 314) {
              obj["override"] = true;
            }
            if (getJsonAltKeyFromField(value.field)) {
              obj[getJsonAltKeyFromField(value.field)] = value.valueAlt;
            }

            if (value._id === "0") {
              setValues({ ...obj });
            } else {
              setValues({ ...values });
            }
            return;
          } else {
            // No field means "replace the whole item", so the value has to be an item.
            // Anything else would put a primitive where the form expects an object.
            if (typeof value.value !== "object" || value.value === null) {
              spErrorTags(`updateValues: non-object whole-item write dropped on item ${value._id}`, ["SAVE"]);
              return;
            }
            obj = value.value;

            if (value._id === "0") {
              setValues({ ...obj });
            } else {
              const firstKey = Object.keys(values)[0];
              if (firstKey) {
                const array = values[firstKey];
                if (Array.isArray(array)) {
                  const index = array.findIndex((el: any) => el._id === obj._id);
                  if (index !== -1) {
                    array[index] = obj;
                  } else {
                    array.push(obj);
                  }
                }
                values[firstKey] = array;
              }

              setValues({ ...values });
            }
            return;
          }
        } else {
          spErrorTags(`updateValues: item not present in values, write dropped: ${value._id} (field ${value.field?.id ?? "none"})`, ["SAVE"]);
        }
      }
    },
    [values],
  );

  const deleteSetValue = useCallback(
    (value: IUpdateValue) => {
      if (value._id) {
        const obj = getObjectById(value._id, values);
        if (obj) {
          if (value.field) {
            delete obj[getJsonKeyFromField(value.field)];

            if (value._id === "0") {
              setValues({ ...obj });
            } else {
              setValues({ ...values });
            }
            return;
          } else {
            return;
          }
        }
      }
    },
    [values],
  );

  const populateItemWithInitialValues = useCallback(
    (fields: ISortedField[]) => {
      const obj: any = {};
      const selectedItinID = bs().selectedItinID;
      for (const field of fields) {
        if (field.field.id === 324) {
          obj[getJsonKeyFromField(field.field)] = true;
          continue;
        }
        if (prefillRules[field.field.id]) {
          const rule = prefillRules[field.field.id];
          switch (rule) {
            case "array":
              obj[getJsonKeyFromField(field.field)] = [];
              break;
            case "itinMembers":
              obj[getJsonKeyFromField(field.field)] = useBoundStore
                .getState()
                .selectedItineraryUsers()
                .map((u) => ({
                  type: "User",
                  option: u.id,
                  _id: section ? generateID(selectedItinID, section.id) : undefined,
                }));
              break;
            case "itinStart":
              obj[getJsonKeyFromField(field.field)] = getEventInfoV2()?.data?.start_date;

              break;
            case "itinEnd":
              obj[getJsonKeyFromField(field.field)] = getEventInfoV2()?.data?.end_date;

              break;
          }
          if (field.children) {
            if (field.field.field_type === FieldTypes.list) {
              obj[getJsonKeyFromField(field.field)].push({
                _id: section?.id !== undefined ? generateID(selectedItinID, section.id) : undefined,
                ...populateItemWithInitialValues(field.children),
              });
            }
          }
        }
      }
      return obj;
    },
    [getEventInfoV2, section],
  );

  const topLevelJsonKey = useCallback(() => {
    const topLevelField = sortedFields?.[0];
    return topLevelField ? getJsonKeyFromField(topLevelField.field) : undefined;
  }, [sortedFields]);

  useEffect(() => {
    // Folds a draft written elsewhere in this section into the current values, not a
    // reload from server data, which would drop any other field mid-edit but not drafted.
    setValues((current: any) => mergeDraftItems(current, topLevelJsonKey(), sectionDraftItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionDraftItems]);

  const draftItem = useCallback(
    async (item: DraftItem) => {
      if (sectionID === undefined || !item) return;
      await bs().addDraftSectionItem(Number.parseInt(sectionID) as SectionId, structuredCloneL(item));
    },
    [sectionID],
  );

  const createNewItem = useCallback(
    (_id: string, idOfList?: string, field?: IField) => {
      const firstField = sortedFields?.[0];
      if (!idOfList) {
        const key = topLevelJsonKey();
        if (!key || !firstField) return;
        if (!values[key]) {
          values[key] = [];
        }
        const pop = populateItemWithInitialValues(firstField.children);
        const newItem = {
          _id: _id,
          ...pop,
        };
        values[key].push(newItem);
        setValues({ ...values });
        recordNewItemSeed(newItem);
        draftItem(newItem);
      } else {
        const obj = getObjectById(idOfList, values);

        if (obj && field) {
          if (field.id === 311 && !firstField) return;
          if (!obj[getJsonKeyFromField(field)]) {
            obj[getJsonKeyFromField(field)] = [];
          }
          let pop = {};
          if (field.id === 311 && firstField) {
            const newRoom = populateItemWithInitialValues(firstField.children);
            pop = newRoom.rooms[0];
          }

          obj[getJsonKeyFromField(field)].push({
            ...pop,
            _id: _id,
          });
          setValues({ ...values });
          draftItem(obj);
        }
      }
    },
    [draftItem, populateItemWithInitialValues, sortedFields, topLevelJsonKey, values],
  );

  // _id may name a nested object (a group, a list entry). The draft holds the top-level
  // entry that owns it.
  const findTopLevelOwner = useCallback(
    (targetId: string) => {
      const key = topLevelJsonKey();
      if (!key) return undefined;
      const top = values[key];
      if (Array.isArray(top)) {
        return top.find((item: any) => getObjectById(targetId, item));
      }
      if (top && typeof top === "object" && getObjectById(targetId, top)) return top;
      return undefined;
    },
    [topLevelJsonKey, values],
  );

  const syncDraftItem = useCallback(
    async (_id: string) => {
      const key = topLevelJsonKey();
      if (!key) return;
      await draftItem(findTopLevelOwner(_id) ?? getObjectById(_id, values[key]));
    },
    [draftItem, findTopLevelOwner, topLevelJsonKey, values],
  );

  // Drops the entry's draft plus the drafts of the nested objects it carries (groups,
  // list entries), which a bare top-level removal would leave behind as orphans.
  const discardDraftItem = useCallback(
    async (_id: string) => {
      if (sectionID === undefined || !_id) return;
      const sectionIdNum = Number.parseInt(sectionID) as SectionId;
      const snapshot =
        bs().draftSectionItems[bs().selectedItinID]?.[sectionIdNum]?.find((d) => d._id === _id) ??
        getObjectById(_id, values);
      if (!snapshot) return;
      // One at a time: each removal persists the draft store.
      for (const nestedId of collectDraftItemIds(snapshot)) {
        await bs().removeDraftSectionItem(sectionIdNum, nestedId);
      }
    },
    [sectionID, values],
  );

  const deleteItem = useCallback(
    (_id: string | string[]) => {
      let newValues = values;

      if (Array.isArray(_id)) {
        _id.forEach((id) => {
          newValues = deleteObjectById(id, newValues);
        });
      } else {
        newValues = deleteObjectById(_id, values);
      }

      setValues({ ...newValues });
      setSectionData((v) => {
        if (v && typeof v === "object" && "data" in v) {
          return { ...v, data: newValues };
        }
        return v; // Return the original value if the type check fails
      });
    },
    [values],
  );
  const duplicateItem = useCallback(
    (_id: string) => {
      const updatedValue = duplicateObject(_id, values);
      setValues({ ...updatedValue });
      setSectionData((v) => {
        if (v && typeof v === "object" && "data" in v) {
          return { ...v, data: updatedValue };
        }
      });
    },
    [values],
  );

  const isNewItem = useCallback((_id: string) => _id !== "0" && !ogIds.current.has(_id), []);

  // Swipe rows can be top-level items or entries nested under one. The draft merge only
  // adds or replaces top-level items by id, so the owner is located before the mutation
  // and a deleted item is never staged.
  const findSwipeOwner = useCallback(
    (targetId: string) => {
      const key = topLevelJsonKey();
      const topLevel = key ? values[key] : undefined;
      if (!Array.isArray(topLevel)) return undefined;
      return topLevel.find((item: any) => getObjectById(targetId, item));
    },
    [topLevelJsonKey, values],
  );

  const applyValues = useCallback((next: any) => {
    setValues({ ...next });
    setSectionData((v) => {
      if (v && typeof v === "object" && "data" in v) {
        return { ...v, data: next };
      }
      return v;
    });
  }, []);

  // Returns true while a confirm dialog is showing, so the card holds the row until it
  // resolves; everything else commits in swipeDeleteCommit once the row has slid out.
  const swipeDeleteItem = useCallback(
    (_id: string) => {
      const owner = findSwipeOwner(_id);
      if (!owner || owner._id !== _id || isNewItem(_id)) return false;

      const sectionName = i18n.t(`sections.${section?.properties?.jsonName}`, { count: 1 });
      const onConfirm = async () => {
        if (!section) return;
        if (await bs().deleteItineraryItem(section.id, _id)) {
          clearDialog();
          discardDraftItem(_id);
          deleteItem(_id);
        } else {
          clearDialog();
          defaultAddSnack(i18n.t("snack.not-deleted-correctly"), "error");
        }
      };
      replaceDialog({
        kind: "confirm",
        text: i18n.t("edit-itinerary.item.delete-item", { sectionName }),
        header: i18n.t("edit-itinerary.item.delete-booking-section", { sectionName }),
        onConfirm,
        confirmBtnStyle: "destructiveBorderless",
        onCancel: () => {
          clearDialog();
        },
        cancelText: i18n.t("general.cancel"),
        confirmText: i18n.t("general.delete"),
        backdropClosable: false,
      });
      return true;
    },
    [clearDialog, deleteItem, discardDraftItem, findSwipeOwner, isNewItem, replaceDialog, section],
  );

  // Exit-time commit for the dialog-less swipe deletes. A top-level new item exists only
  // as a staged draft, so dropping the draft is the whole delete.
  const swipeDeleteCommit = useCallback(
    (_id: string) => {
      const owner = findSwipeOwner(_id);
      if (owner && owner._id !== _id) {
        const newValues = deleteObjectById(_id, values);
        applyValues(newValues);
        draftItem(getObjectById(owner._id, newValues));
        return;
      }
      if (isNewItem(_id)) {
        discardDraftItem(_id);
        deleteItem(_id);
      }
    },
    [applyValues, deleteItem, discardDraftItem, draftItem, findSwipeOwner, isNewItem, values],
  );

  const swipeDuplicateItem = useCallback(
    (_id: string) => {
      const owner = findSwipeOwner(_id);

      const updatedValue = duplicateObject(_id, values);
      applyValues(updatedValue);

      if (owner && owner._id !== _id) {
        // The copy sits inside the owner, so the owner's draft carries it.
        draftItem(getObjectById(owner._id, updatedValue));
        return;
      }

      // Mirrors createNewItem: the copy takes a fresh id, is staged, then opens its form.
      const key = topLevelJsonKey();
      if (!key) return;
      const next = updatedValue[key];
      if (!Array.isArray(next)) return;
      const previousIds = new Set(Array.isArray(values[key]) ? values[key].map((item: any) => item._id) : []);
      const copy = next.find((item: any) => !previousIds.has(item._id));
      if (!copy) return;
      recordNewItemSeed(copy);
      draftItem(copy);
      if (section) {
        pushPath(routes.edit.sectionItem.build({ sectionId: section.id, itemId: copy._id }));
      }
    },
    [applyValues, draftItem, findSwipeOwner, pushPath, section, topLevelJsonKey, values],
  );

  const prettyPrintValue = useCallback((field: IField, value: any) => {
    if (field.field_type === FieldTypes.text && field.type_properties?.data_source === "users") {
      const parsed = tryParseJSON(value);
      const user = bs().getUserFromID(parsed.option);
      if (user) {
        return user.firstName + " " + user.lastName;
      } else {
        return "";
      }
    }
    if (field.field_type === FieldTypes.select && field.type_properties?.data_source === "usersWithTags") {
      const parsed = tryParseJSON(value);
      return parsed.map((u: any) => {
        if (u.type === "User") {
          const user = useBoundStore.getState().getUserFromID(u.option);
          return user ? bs().getNameFormatted(user) : "";
        } else {
          return u.option;
        }
      });
    }
    return value;
  }, []);

  const getValue = useCallback(
    (field: IField, _id?: string, prettyPrint?: boolean, altValue: boolean = false) => {
      if (section === undefined || !field) {
        return "";
      }

      let key = getJsonKeyFromField(field);

      if (altValue) {
        key = getJsonAltKeyFromField(field);
      }

      if (field.parent === null) {
        if (values && values.hasOwnProperty(key)) return values[key];
        if (field.field_type === FieldTypes.list) {
          return [];
        }
        if (field.field_type === FieldTypes.group) {
          return {};
        }

        return null;
      }
      if (_id) {
        const obj = getObjectById(_id, values);
        if (obj?.hasOwnProperty(key)) {
          if (prettyPrint) {
            return prettyPrintValue(field, obj[key]);
          }
          return obj[key];
        } else {
          if (field.field_type === FieldTypes.list) {
            return [];
          }
          if (field.field_type === FieldTypes.group) {
            return {};
          }
        }

        return "";
      }

      return "";
    },
    [values, section, prettyPrintValue],
  );

  const isListTypeSection = useMemo(() => {
    if (sortedFields === undefined || sortedFields.length === 0) return false;
    return sortedFields[0]!.field.field_type === FieldTypes.list;
  }, [sortedFields]);

  const onCancelEdit = () => {
    if (!sectionData) return;
    const hardCopy = JSON.parse(JSON.stringify(ogValues.current));
    setValues(hardCopy);
    sectionData.data = hardCopy;
    const copySectionData = structuredCloneL(sectionData);
    setSectionData(copySectionData);
  };

  // Pure validation + error-surfacing (red fields), no save.
  const validateForSave = (_id: string): boolean => {
    if (!isPathValid(_id)) {
      const invalidFieldIds = fieldStates.filter((f) => f._id === _id && !f.isValid).map((f) => f.id);
      spErrorTags(
        `Fields not valid for saving: ${invalidFieldIds.join(", ") || "(none registered - stale isPathValid?)"}`,
        ["SAVE"],
      );
      setShowErrors(true);
      defaultAddSnack(i18n.t("edit-itinerary.item.invalid-fields-error"), "error");
      return false;
    }
    // The server receives only the item Done was pressed on, so completeness is checked
    // for that item alone, nested entries included. A booking's edit screen stages into
    // this provider's values only, so this is the last validation before the item
    // reaches the server.
    if (isListTypeSection) {
      const listField = sortedFields?.[0];
      const edited = getObjectById(_id, values);
      if (!edited || edited === values) {
        spErrorTags(`Item not present in values, cannot validate: ${_id}`, ["SAVE"]);
        setShowErrors(true);
        defaultAddSnack(i18n.t("edit-itinerary.item.invalid-fields-error"), "error");
        return false;
      }
      const allFieldsComplete = listField!.children.every((child) =>
        isFieldTreeComplete(child, _id, getValue),
      );
      if (!allFieldsComplete) {
        // The specific empty required field was already logged inside isFieldTreeComplete.
        spErrorTags("Item incomplete for saving", ["SAVE"]);
        setShowErrors(true);
        defaultAddSnack(i18n.t("edit-itinerary.item.invalid-fields-error"), "error");
        return false;
      }
      return true;
    }

    const itemFields = sortedFields?.[0]?.children ?? [];
    if (!itemFields.every((child) => isFieldTreeComplete(child, _id, getValue))) {
      // The specific empty required field was already logged inside isFieldTreeComplete.
      spErrorTags("Item incomplete for saving", ["SAVE"]);
      setShowErrors(true);
      defaultAddSnack(i18n.t("edit-itinerary.item.invalid-fields-error"), "error");
      return false;
    }
    return true;
  };

  const onSaveData = async (_id: string, options?: { skipValidation?: boolean; skipNavigation?: boolean }) => {
    let saveData;
    if (!options?.skipValidation && !validateForSave(_id)) {
      return false;
    }
    setValidationOn(false);
    // showErrors (unlike validationOn) is what actually renders red fields, and the data
    // reset below can transiently empty fields while the view is still animating out.
    setShowErrors(false);
    if (_id === "0") {
      saveData = values;
    } else {
      saveData = getObjectById(_id, values);
    }

    const result = section ? await updateItinerarySection(section.id, saveData) : "error";
    if (result === "success") {
      if (section) {
        // The saved entry is server truth now; drop its draft and the drafts of the
        // nested objects it carries, so none of them merge over the refetched data.
        for (const nestedId of collectDraftItemIds(saveData)) {
          await bs().removeDraftSectionItem(section.id as SectionId, nestedId);
        }
      }
      nowSetAlreadyPopulated(false);
      initLoad();
      if (!options?.skipNavigation) {
        goBackInView();
      }
      return true;
    } else if (result === "private_data_error") {
      defaultAddSnack(i18n.t("edit-itinerary.item.no-edit-access-error"), "error");
      return false;
    } else {
      defaultAddSnack(i18n.t("snack.not-added-correctly"), "error");
      return false;
    }
  };

  // Per item, as saving is: another item's draft in the same section - such as a new item
  // abandoned unsaved - is not this item's change, and would otherwise hold every item dirty.
  // A new item compares against its seed, so an untouched one has nothing pending.
  const hasPendingChanges = useCallback(
    (_id: string) => {
      const current = findTopLevelEntry(values, _id) ?? getObjectById(_id, values);
      if (isNewItem(_id)) return differsFromNewItemSeed(_id, current);
      const original = findTopLevelEntry(ogValues.current, _id) ?? getObjectById(_id, ogValues.current);
      return JSON.stringify(current) !== JSON.stringify(original);
    },
    [isNewItem, values],
  );

  const nowSetAlreadyPopulated = (ans: boolean) => {
    setAlreadyPopulated(ans);
  };

  return (
    <EditSectionContext.Provider
      value={{
        sectionData,
        section,
        updateValues,
        deleteSetValue,
        createNewItem,
        syncDraftItem,
        discardDraftItem,
        getValue,
        values,
        isListTypeSection,
        onCancelEdit,
        validateForSave,
        onSaveData,
        hasPendingChanges,
        isNewItem,
        itineraryTitle,
        nowSetAlreadyPopulated,
        alreadyPopulated,
        deleteItem,
        duplicateItem,
        swipeDeleteItem,
        swipeDeleteCommit,
        swipeDuplicateItem,
      }}
    >
      {sectionData && props.children}
    </EditSectionContext.Provider>
  );
};

export const useEditSectionContext = () => {
  return useContext(EditSectionContext);
};
